import { useEffect, useState } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { Warning } from '@material-ui/icons';
import { isEmpty, isNil, map, reject } from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import Cookies from 'js-cookie';

import { useGlobalDataService } from '../../services/ApiDataService';
import { rethrowError } from '../../utilities/RequestUtilities';
import { RowFlexLayout } from '../layout';
import useSingletonValue from '../../utilities/UseSingletonValue';

const WARNINGS_URL = '/ngp/v2/getWarningsIfAny';

const useStyles = makeStyles((theme) => ({
    warning: {
        color: theme.palette.warning.light,
    },
}));

export default function LoginWarnings() {
    const apiWarnings = useApiLoginWarnings();
    const loginWarnings = useLoginWarnings();
    const classes = useStyles();

    const warnings = [...(apiWarnings || []), ...(loginWarnings || [])];

    return map(warnings, (warning) => (
        <Box key={warning} mb={2}>
            <RowFlexLayout vCenter>
                <Warning className={classes.warning} />
                <Typography className={classes.warning} variant="subtitle2" component={Box} ml={2}>
                    {warning}
                </Typography>
            </RowFlexLayout>
        </Box>
    ));
}

function useApiLoginWarnings() {
    const dataService = useGlobalDataService();
    const [warnings, setWarnings] = useState([]);

    useEffect(() => {
        getWarnings();

        async function getWarnings() {
            try {
                const { warnings: apiWarnings } = await dataService.get(WARNINGS_URL, null, null, rethrowError);

                setWarnings(reject(apiWarnings, isNil));
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to load system warnings: ', e);
                setWarnings([]);
            }
        }
    }, [dataService]);

    return warnings;
}

const IS_FORBIDDEN_COOKIE = 'isForbidden'; // this one is handled a little differently...
const SSO_ERROR_COOKIE = 'ssoError';
const NG1_ERROR_COOKIE = 'ng1QueryError';
const WARNING_COOKIES = [SSO_ERROR_COOKIE, NG1_ERROR_COOKIE];

function useLoginWarnings() {
    return useSingletonValue(() => {
        const warnings = reject(
            map(WARNING_COOKIES, (cookie) => Cookies.get(cookie)),
            isNil
        );

        // MattN - This cookie gets set somewhere along the line
        const isForbidden = Cookies.get(IS_FORBIDDEN_COOKIE);
        if (!isNil(isForbidden) && !isEmpty(isForbidden)) {
            warnings.push(
                i18n._(
                    t`This account does not have the proper authorization, please login again. If you continue to have issues contact support.`
                )
            );
        }

        // Wipe out all of these cookies
        WARNING_COOKIES.forEach((cookie) => Cookies.remove(cookie));
        Cookies.remove(IS_FORBIDDEN_COOKIE);

        return warnings;
    });
}
